import {ReactElement, useCallback} from 'react';
import {
  useCart,
  CartLineImage,
  CartLinePrice,
  CartLineProductTitle,
  CartLineQuantity,
  CartLineQuantityAdjustButton,
  Image,
  Link,
  useCartLine,
} from '@shopify/hydrogen';
import classNames from 'classnames';

import {useCartUI} from '../../hooks';
import {PlaceholderWhiskyJPEG} from '../../assets/img';

export default function CartLineItem(): ReactElement {
  const {
    merchandise: {
      image,
      title: shopTitle,
      product: {handle, title},
    },
    quantity,
  } = useCartLine() as ReturnType<typeof useCartLine>;

  const {closeCart} = useCartUI();

  return (
    <div className="cart-preview__item">
      <Link to={`/shop/${handle}/`} className="cart-preview__item__image">
        {image ? (
          <CartLineImage width={320} height={400} />
        ) : (
          <Image
            src={PlaceholderWhiskyJPEG}
            width={320}
            height={400}
            alt={title}
          />
        )}
      </Link>
      <div className="cart-preview__item__info">
        <h4 className="cart-preview__item__title">
          <CartLineProductTitle
            as={Link}
            onClick={closeCart}
            to={`/shop/${handle}/`}
          />
        </h4>
        <div className="cart-preview__item__seller">{shopTitle}</div>
        <div className="quantity-container js-quantity-container">
          <CartLineQuantityAdjustButton
            className={classNames('quantity-decrease', {
              disabled: quantity <= 1,
            })}
            disabled={quantity <= 1}
            adjust="decrease"
            type="button"
            title="Decrease Quantity"
          >
            <span className="minus" />
          </CartLineQuantityAdjustButton>
          <CartLineQuantity className="quantity-amount js-quantity-amount" />
          <CartLineQuantityAdjustButton
            className="quantity-increase"
            adjust="increase"
            type="button"
            title="Increase Quantity"
          >
            <span className="plus" />
          </CartLineQuantityAdjustButton>
        </div>
      </div>
      <div className="cart-preview__item__right">
        <div className="cart-preview__item__price">
          $<CartLinePrice as="span" withoutCurrency />
        </div>
        <div className="cart-preview__item__delete">
          <CartLineRemoveButton />
        </div>
      </div>
    </div>
  );
}

export function CartLineRemoveButton() {
  const {status, linesUpdate} = useCart();
  const cartLine = useCartLine();

  const handleClick = useCallback(
    // We are using the the lineUpdate method instead of linesRemove so
    // that the analytics event contains the old and new cart so that we
    // can find the product variant's id
    () => linesUpdate([{id: cartLine.id, quantity: 0}]),
    [cartLine.id, linesUpdate],
  );

  return (
    <button
      type="button"
      title="Remove from Cart"
      disabled={status !== 'idle'}
      onClick={handleClick}
    >
      <i className="icon-error" />
    </button>
  );
}
